/**
 * Enterprise Dashboard Types
 * TypeScript interfaces for enterprise usage tracking
 */

export type {
  CostData,
  CostByModel,
  UseCaseCostItem,
  UseCaseRunCount,
  UserCostData,
} from '../freeTier/types';

import type { CostData, UseCaseCostItem, UseCaseRunCount, UserCostData } from '../freeTier/types';

export interface FeatureRun {
  period: string;
  request_type: string;
  runs: number;
}

export interface UseCaseData {
  usecase: string;
  total_runs: number;
  unique_users: number;
  active_days: number;
}

export interface EnterpriseCustomer {
  id: number;
  email: string;
  name: string | null;
  total_runs: number;
  last_activity: string | null;
  primary_usecase: string | null;
}

export interface CustomerDetail {
  id: number;
  email: string;
  name: string | null;
  subscription_plan: string;
  subscription_status: string;
  subscription_start_date: string | null;
  created_at: string;
  monthly_simulation_count: number;
  total_audience_requests: number;
}

export interface ActivityLogEntry {
  id: number;
  created_at: string;
  request_type: string;
  usecase: string | null;
  status: string;
  model: string;
  time_taken_ms: number | null;
  input_tokens: number | null;
  output_tokens: number | null;
  estimated_cost: number | null;
}

export interface CustomerAudience {
  id: number;
  name: string;
  industry: string | null;
  created_at: string;
  segment_count: number;
  persona_count: number;
}

export interface CustomerSimulation {
  id: number;
  name: string;
  use_case: string | null;
  created_at: string;
}

export interface SummaryStats {
  total_runs: number;
  active_customers: number;
  total_enterprise_customers: number;
  most_used_feature: string | null;
  least_used_feature: string | null;
  feature_breakdown: Record<string, number>;
}

export interface EnterpriseDashboardData {
  time_range: string;
  granularity: string;
  start_date: string;
  summary: SummaryStats;
  feature_runs: FeatureRun[];
  usecase_breakdown: UseCaseData[];
  customers: EnterpriseCustomer[];
  cost_data: CostData;
  usecase_cost_breakdown: UseCaseCostItem[];
  usecase_run_counts: UseCaseRunCount[];
  cost_by_user: UserCostData[];
}

export interface CustomerDetailResponse {
  time_range: string;
  granularity: string;
  start_date: string;
  customer: CustomerDetail;
  feature_runs: FeatureRun[];
  usecase_breakdown: UseCaseData[];
  activity_log: ActivityLogEntry[];
  audiences: CustomerAudience[];
  simulations: CustomerSimulation[];
  cost_data: CostData;
  usecase_cost_breakdown: UseCaseCostItem[];
  usecase_run_counts: UseCaseRunCount[];
}

export type TimeRange = '24h' | '7d' | '30d' | '90d' | '1y';

export interface TimeRangeOption {
  value: TimeRange;
  label: string;
  granularity: string;
}

export const TIME_RANGE_OPTIONS: TimeRangeOption[] = [
  { value: '24h', label: '24 Hours', granularity: 'hour' },
  { value: '7d', label: '7 Days', granularity: 'day' },
  { value: '30d', label: '30 Days', granularity: 'day' },
  { value: '90d', label: '90 Days', granularity: 'week' },
  { value: '1y', label: '1 Year', granularity: 'month' },
];

export const FEATURE_LABELS: Record<string, string> = {
  simulation_analysis: 'Simulations',
  group_chat: 'Group Chats',
  healthcare_chat: 'Healthcare Chat',
  healthcare_v2: 'Healthcare v2',
  persona_chat: 'Persona Chats',
  survey_response: 'Survey Responses',
  persona_generation: 'Persona Generation',
  segment_generation: 'Segment Generation',
  summary_generation: 'Summaries',
};

export const USECASE_LABELS: Record<string, string> = {
  Survey: 'Survey & Focus Groups',
  'Packaging Test': 'Packaging Test',
  'AB Test': 'AB Test / Multivariate',
  'Creative Test': 'Creative Test',
  'Pricing Test': 'Pricing Test',
  'Concept Test': 'Concept Test',
  'Claims Test': 'Claims Test',
  'Branding Test': 'Branding Test',
  'Messaging Test': 'Messaging Test',
  'Healthcare v2': 'Healthcare v2',
  audience: 'Audience',
  simulation: 'Simulation',
  chat: 'Chat',
  group_chat: 'Group Chat',
};
